// Note type definitions
export type NoteType = 'quick' | 'meeting' | 'idea' | 'reference' | 'journal';

// Client reference (populated)
export interface NoteClient {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

// Creator reference (populated)
export interface NoteCreator {
  _id: string;
  profile: {
    firstName: string;
    lastName: string;
  };
}

// Task reference (for converted notes)
export interface NoteTask {
  _id: string;
  title: string;
  status: string;
}

// Main Note interface
export interface Note {
  _id: string;
  organizationId: string;
  createdById: string | NoteCreator;

  // Core fields
  title: string;
  content: string;
  type: NoteType;

  // Organization
  isPinned: boolean;
  isArchived: boolean;
  color?: string;
  tags: string[];

  // Relationships
  clientId?: string | NoteClient;
  projectId?: string;
  bookingId?: string;

  // Conversion tracking
  convertedToTaskId?: string | NoteTask;
  convertedAt?: string;

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

// Note with populated fields (for list views)
export interface NoteWithRelations extends Omit<Note, 'clientId' | 'createdById' | 'convertedToTaskId'> {
  clientId?: NoteClient;
  createdById?: NoteCreator;
  convertedToTaskId?: NoteTask;
}

// Create note request
export interface CreateNoteRequest {
  title: string;
  content?: string;
  type?: NoteType;
  color?: string;
  tags?: string[];
  clientId?: string;
  projectId?: string;
  bookingId?: string;
}

// Update note request
export interface UpdateNoteRequest {
  title?: string;
  content?: string;
  type?: NoteType;
  color?: string;
  tags?: string[];
  clientId?: string | null;
  projectId?: string | null;
  bookingId?: string | null;
}

// Note filters
export interface NoteFilters {
  type?: NoteType[];
  clientId?: string;
  pinned?: boolean;
  archived?: boolean | 'all';
  tags?: string[];
  sortBy?: 'createdAt' | 'updatedAt' | 'title';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// Pagination
export interface NotePagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

// Notes list response
export interface NotesResponse {
  success: boolean;
  data: {
    notes: NoteWithRelations[];
    pagination: NotePagination;
  };
}

// Single note response
export interface NoteResponse {
  success: boolean;
  data: {
    note: Note;
  };
}

// Search response
export interface NoteSearchResponse {
  success: boolean;
  data: {
    notes: NoteWithRelations[];
    query: string;
    count: number;
  };
}

// Convert to task response
export interface ConvertToTaskResponse {
  success: boolean;
  data: {
    note: Note;
    task: {
      _id: string;
      title: string;
      status: string;
    };
    message: string;
  };
}

// Note type options for UI
export const NOTE_TYPE_OPTIONS: { value: NoteType; label: string; icon: string }[] = [
  { value: 'quick', label: 'Quick Note', icon: '⚡' },
  { value: 'meeting', label: 'Meeting Notes', icon: '👥' },
  { value: 'idea', label: 'Idea', icon: '💡' },
  { value: 'reference', label: 'Reference', icon: '📌' },
  { value: 'journal', label: 'Journal', icon: '📔' },
];

// Note color options
export const NOTE_COLORS = [
  '#FFE082', // Yellow
  '#FFAB91', // Orange
  '#EF9A9A', // Red
  '#CE93D8', // Purple
  '#90CAF9', // Blue
  '#80DEEA', // Cyan
  '#A5D6A7', // Green
  '#BCAAA4', // Brown
];
